import React, { useState, useEffect } from 'react';
import { collectorAPI } from '../services/api';
import { CollectorDashboard as CollectorDashboardType, CollectionRequest, CollectorStatus } from '../types/collection';
import { CollectionStatusLabels, CollectorStatusLabels } from '../types/collection';
import './CollectorDashboard.css';

const CollectorDashboard: React.FC = () => {
  const [dashboard, setDashboard] = useState<CollectorDashboardType | null>(null);
  const [assignedRequests, setAssignedRequests] = useState<CollectionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadAssignedRequests();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await collectorAPI.getMyDashboard();
      setDashboard(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || '대시보드 로딩 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignedRequests = async () => {
    try {
      const data = await collectorAPI.getMyRequests('confirmed,in_progress');
      setAssignedRequests(data);
    } catch (err: any) {
      console.error('배정된 요청 로딩 실패:', err);
    }
  };

  const updateStatus = async (newStatus: CollectorStatus) => {
    if (!dashboard) return;
    
    setStatusUpdating(true);
    try {
      await collectorAPI.updateMyStatus({ status: newStatus });
      await loadDashboard(); // 대시보드 새로고침
    } catch (err: any) {
      setError(err.response?.data?.detail || '상태 업데이트 중 오류가 발생했습니다');
    } finally {
      setStatusUpdating(false);
    }
  };

  const updateLocation = async () => {
    if (!navigator.geolocation) {
      setError('위치 서비스가 지원되지 않습니다');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await collectorAPI.updateMyLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          await loadDashboard();
        } catch (err: any) {
          setError(err.response?.data?.detail || '위치 업데이트 중 오류가 발생했습니다');
        }
      },
      (error) => {
        setError('위치 정보를 가져올 수 없습니다');
      }
    );
  };

  const startRequest = async (requestId: number) => {
    try {
      await collectorAPI.updateMyStatus({
        status: CollectorStatus.ON_DUTY
      });
      // 수거 요청을 진행중으로 변경하는 API가 필요하지만 현재는 없으므로 새로고침만
      await loadAssignedRequests();
      await loadDashboard();
    } catch (err: any) {
      setError(err.response?.data?.detail || '작업 시작 중 오류가 발생했습니다');
    }
  };

  const completeRequest = async (requestId: number) => {
    const weight = window.prompt('실제 수거 무게(kg)를 입력하세요:');
    const notes = window.prompt('완료 메모를 입력하세요 (선택사항):');
    
    try {
      await collectorAPI.completeRequest(requestId, {
        actual_weight: weight ? parseFloat(weight) : undefined,
        completion_notes: notes || undefined
      });
      await loadAssignedRequests();
      await loadDashboard();
    } catch (err: any) {
      setError(err.response?.data?.detail || '수거 완료 처리 중 오류가 발생했습니다');
    }
  };

  if (loading) {
    return <div className="collector-dashboard loading">로딩 중...</div>;
  }

  if (error) {
    return <div className="collector-dashboard error">오류: {error}</div>;
  }

  if (!dashboard) {
    return <div className="collector-dashboard error">대시보드 데이터를 찾을 수 없습니다</div>;
  }

  const collector = dashboard.collector_info;

  return (
    <div className="collector-dashboard">
      <div className="dashboard-header">
        <h1>수거 작업자 대시보드</h1>
        <div className="collector-info">
          <h2>{collector.employee_id} - {collector.phone}</h2>
          <div className="status-info">
            <span className={`status-badge ${collector.status}`}>
              {CollectorStatusLabels[collector.status]}
            </span>
            <span className={`availability ${collector.is_available ? 'available' : 'unavailable'}`}>
              {collector.is_available ? '배정 가능' : '배정 불가'}
            </span>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="dashboard-stats">
        <div className="stat-card">
          <h3>오늘 완료</h3>
          <div className="stat-value">{dashboard.completed_today}</div>
          <div className="stat-label">건</div>
        </div>
        <div className="stat-card">
          <h3>오늘 수거량</h3>
          <div className="stat-value">{dashboard.total_weight_today.toFixed(1)}</div>
          <div className="stat-label">kg</div>
        </div>
        <div className="stat-card">
          <h3>배정된 작업</h3>
          <div className="stat-value">{dashboard.assigned_requests_count}</div>
          <div className="stat-label">건</div>
        </div>
        <div className="stat-card">
          <h3>적재율</h3>
          <div className="stat-value">{dashboard.current_capacity_usage.toFixed(1)}</div>
          <div className="stat-label">%</div>
        </div>
      </div>

      <div className="dashboard-actions">
        <div className="status-controls">
          <h3>상태 관리</h3>
          <div className="status-buttons">
            <button 
              onClick={() => updateStatus(CollectorStatus.ON_DUTY)}
              disabled={statusUpdating}
              className={collector.status === CollectorStatus.ON_DUTY ? 'active' : ''}
            >
              근무 시작
            </button>
            <button 
              onClick={() => updateStatus(CollectorStatus.OFF_DUTY)}
              disabled={statusUpdating}
              className={collector.status === CollectorStatus.OFF_DUTY ? 'active' : ''}
            >
              근무 종료
            </button>
            <button 
              onClick={() => updateStatus(CollectorStatus.UNAVAILABLE)}
              disabled={statusUpdating}
              className={collector.status === CollectorStatus.UNAVAILABLE ? 'active' : ''}
            >
              사용 불가
            </button>
          </div>
        </div>
        
        <div className="location-controls">
          <h3>위치 관리</h3>
          <button onClick={updateLocation} className="location-btn">
            📍 현재 위치 업데이트
          </button>
          {collector.last_location_update && (
            <div className="last-update">
              마지막 업데이트: {new Date(collector.last_location_update).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      <div className="assigned-requests">
        <h3>배정된 수거 요청</h3>
        {assignedRequests.length === 0 ? (
          <div className="no-requests">배정된 요청이 없습니다</div>
        ) : (
          <div className="requests-list">
            {assignedRequests.map((request) => (
              <div key={request.id} className="request-card">
                <div className="request-header">
                  <span className="request-id">#{request.id}</span>
                  <span className={`status-badge ${request.status}`}>
                    {CollectionStatusLabels[request.status]}
                  </span>
                </div>
                <div className="request-details">
                  <div className="address">📍 {request.collection_address}</div>
                  <div className="description">{request.description}</div>
                  {request.estimated_weight && (
                    <div className="weight">예상 무게: {request.estimated_weight}kg</div>
                  )}
                  {request.collection_date && (
                    <div className="date">
                      희망일: {new Date(request.collection_date).toLocaleDateString()}
                    </div>
                  )}
                  {request.special_instructions && (
                    <div className="instructions">특별 지시: {request.special_instructions}</div>
                  )}
                </div>
                <div className="request-actions">
                  {request.status === 'confirmed' && (
                    <button 
                      onClick={() => startRequest(request.id)}
                      className="start-btn"
                    >
                      작업 시작
                    </button>
                  )}
                  {request.status === 'in_progress' && (
                    <button 
                      onClick={() => completeRequest(request.id)}
                      className="complete-btn"
                    >
                      수거 완료
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="view-all-tasks">
          <a href="/collector-tasks" className="view-all-btn">
            모든 배정된 작업 보기 →
          </a>
        </div>
      </div>

      <div className="collector-profile">
        <h3>내 프로필</h3>
        <div className="profile-info">
          <div className="info-row">
            <span className="label">직원 ID:</span>
            <span className="value">{collector.employee_id}</span>
          </div>
          <div className="info-row">
            <span className="label">연락처:</span>
            <span className="value">{collector.phone}</span>
          </div>
          {collector.vehicle_number && (
            <div className="info-row">
              <span className="label">차량 번호:</span>
              <span className="value">{collector.vehicle_number}</span>
            </div>
          )}
          {collector.work_area && (
            <div className="info-row">
              <span className="label">담당 지역:</span>
              <span className="value">{collector.work_area}</span>
            </div>
          )}
          <div className="info-row">
            <span className="label">최대 용량:</span>
            <span className="value">{collector.max_capacity}kg</span>
          </div>
          <div className="info-row">
            <span className="label">현재 적재량:</span>
            <span className="value">{collector.current_load}kg</span>
          </div>
          <div className="info-row">
            <span className="label">총 수거 건수:</span>
            <span className="value">{collector.total_collections}건</span>
          </div>
          <div className="info-row">
            <span className="label">총 수거량:</span>
            <span className="value">{collector.total_weight_collected.toFixed(1)}kg</span>
          </div>
          {collector.rating > 0 && (
            <div className="info-row">
              <span className="label">평점:</span>
              <span className="value">⭐ {collector.rating.toFixed(1)} ({collector.rating_count}개 평가)</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectorDashboard;